/**
 * MSAL Configuration
 *
 * Azure B2C configuration for authentication.
 */
import type { Configuration, PopupRequest } from '@azure/msal-browser';

/**
 * Get the B2C authority hostname based on current origin
 */
function getAuthorityHostname(): string {
  if (typeof window === 'undefined') {
    return 'persuastionstudiesb2c.b2clogin.com';
  }

  const origin = window.location.origin;

  if (origin === 'https://dojo.objectively.win') {
    return 'login.objectively.win';
  }

  if (origin === 'https://dojo.objectively.ai') {
    return 'login.objectively.ai';
  }

  // Default for localhost and Azure deployments
  return 'persuastionstudiesb2c.b2clogin.com';
}

/**
 * Azure B2C configuration constants
 */
export const B2C_CONFIG = {
  clientId: '262d39db-b6ac-4a90-94de-3a864f450285',
  tenant: 'PersuastionStudiesB2C.onmicrosoft.com',
  signUpSignInPolicy: 'B2C_1_SignUp_SignIn',
} as const;

/**
 * Build the B2C authority URL
 */
export function getAuthority(): string {
  const hostname = getAuthorityHostname();
  return `https://${hostname}/${B2C_CONFIG.tenant}/${B2C_CONFIG.signUpSignInPolicy}`;
}

/**
 * Build the known authorities list for MSAL
 */
export function getKnownAuthorities(): string[] {
  const hostname = getAuthorityHostname();
  return [
    `https://${hostname}/${B2C_CONFIG.tenant}/v2.0/.well-known/openid-configuration?p=${B2C_CONFIG.signUpSignInPolicy}`,
  ];
}

/**
 * Get the redirect URI (current origin)
 */
function getRedirectUri(): string {
  if (typeof window === 'undefined') {
    return 'http://localhost:8081/';
  }
  return `${window.location.origin}/`;
}

/**
 * Get the API scopes for token requests
 */
export function getScopes(): string[] {
  return [
    `https://${B2C_CONFIG.tenant}/${B2C_CONFIG.clientId}/BackendApi`,
  ];
}

/**
 * Get storage key prefix for clearing B2C data
 */
export function getStorageKeyPrefix(): string {
  return getAuthorityHostname() + '-';
}

/**
 * Create MSAL configuration
 */
export function createMsalConfig(): Configuration {
  return {
    auth: {
      clientId: B2C_CONFIG.clientId,
      authority: getAuthority(),
      redirectUri: getRedirectUri(),
      postLogoutRedirectUri: getRedirectUri(),
      knownAuthorities: getKnownAuthorities(),
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? 3 : 0, // Info in dev, Error in prod
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (import.meta.env.DEV) {
            console.debug(`[MSAL] ${message}`);
          }
        },
      },
    },
  };
}

/**
 * Create popup request for sign-in
 * @param idpHint - Optional identity provider hint (e.g. 'google.com') to skip B2C provider selection
 */
export function createLoginRequest(idpHint?: string): PopupRequest {
  return {
    scopes: getScopes(),
    ...(idpHint && {
      extraQueryParameters: { domain_hint: idpHint },
    }),
  };
}

/**
 * Create silent token request
 */
export function createSilentRequest(): PopupRequest {
  return {
    scopes: getScopes(),
  };
}
